import React from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BASE_URL } from "../services/config";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { Accordion, AccordionItem } from "@heroui/react";
import heroBackground from '../assets/images/returns-banner.png';

export const ReturnsPage: React.FC = () => {
  const location = useLocation();

  return (
    <>
      <Helmet>
        <title>Возврат и обмен — SAFONOVA</title>
        <meta name="description" content="Правила возврата и обмена товаров SAFONOVA: сроки, условия и процедура возврата" />
        <link rel="canonical" href={`${BASE_URL || ''}${location.pathname}${location.search || ''}`} />
        <meta property="og:title" content="Возврат и обмен — SAFONOVA" />
        <meta property="og:description" content="Правила возврата и обмена товаров SAFONOVA: сроки, условия и процедура возврата" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${BASE_URL || ''}${location.pathname}${location.search || ''}`} />
        <meta property="og:image" content={`${BASE_URL || ''}/og-default.png`} />
      </Helmet>
      {/* Hero Section */}
      <section 
        // className="py-24 bg-content2"
        className="relative flex items-center py-32 md:py-40"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.10)), url('${heroBackground}')`,
          backgroundPosition: "center 40%",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat"
        }}>
        <div className="container mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white text-shadow">
              Возврат и <span className="font-medium">обмен</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl text-shadow">
              Информация о правилах возврата и обмена товаров
            </p>
          </motion.div>
        </div>
      </section>
      {/* <section className="py-24 bg-content2">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-light mb-6">
              Возврат и <span className="font-medium">обмен</span>
            </h1>
            <p className="text-lg text-foreground/80 max-w-2xl mx-auto">
              Информация о правилах возврата и обмена товаров
            </p>
          </motion.div>
        </div>
      </section> */}

      {/* Returns Policy */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <h2 className="text-3xl font-light mb-6">Условия <span className="font-medium">возврата</span></h2>
              <p className="text-foreground/80 mb-6">
                Мы стремимся к тому, чтобы вы были полностью удовлетворены вашей покупкой. Если по какой-либо причине вы не удовлетворены, мы предлагаем простую процедуру возврата и обмена.
              </p>
              
              <div className="space-y-6 mt-8">
                <div className="border border-divider p-6">
                  <h3 className="text-xl font-medium mb-4">Возврат товара надлежащего качества</h3>
                  <p className="text-foreground/80 mb-4">
                    Вы можете вернуть товар надлежащего качества в течение 14 дней с момента получения при соблюдении следующих условий:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Icon icon="lucide:check" className="text-primary mt-1" width={18} />
                      <span className="text-foreground/80">Сохранены товарный вид, потребительские свойства, пломбы, фабричные ярлыки</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Icon icon="lucide:check" className="text-primary mt-1" width={18} />
                      <span className="text-foreground/80">Имеется документ, подтверждающий факт покупки (чек или электронный чек)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Icon icon="lucide:check" className="text-primary mt-1" width={18} />
                      <span className="text-foreground/80">Товар не был в употреблении</span>
                    </li>
                  </ul>
                </div>

                <div className="border border-divider p-6">
                  <h3 className="text-xl font-medium mb-4">Возврат товара ненадлежащего качества</h3>
                  <p className="text-foreground/80 mb-4">
                    Если вы обнаружили брак или дефект в товаре, вы имеете право:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Icon icon="lucide:check" className="text-primary mt-1" width={18} />
                      <span className="text-foreground/80">Потребовать замены товара на аналогичный</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Icon icon="lucide:check" className="text-primary mt-1" width={18} />
                      <span className="text-foreground/80">Потребовать соразмерного уменьшения покупной цены</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Icon icon="lucide:check" className="text-primary mt-1" width={18} />
                      <span className="text-foreground/80">Потребовать возврата уплаченной за товар суммы</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-light mb-6">Процедура <span className="font-medium">возврата</span></h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="border border-divider p-6 text-center"
                >
                  <div className="bg-primary/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                    <Icon icon="lucide:message-square" className="text-primary" width={24} />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Шаг 1</h3>
                  <p className="text-foreground/80">
                    Свяжитесь с нами по телефону или email для оформления заявки на возврат
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="border border-divider p-6 text-center"
                >
                  <div className="bg-primary/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                    <Icon icon="lucide:package" className="text-primary" width={24} />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Шаг 2</h3>
                  <p className="text-foreground/80">
                    Упакуйте товар в оригинальную упаковку или надежную коробку
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="border border-divider p-6 text-center"
                >
                  <div className="bg-primary/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                    <Icon icon="lucide:credit-card" className="text-primary" width={24} />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Шаг 3</h3>
                  <p className="text-foreground/80">
                    Получите возврат денежных средств тем же способом, которым была произведена оплата
                  </p>
                </motion.div>
              </div>

              <div className="bg-content2/50 p-6 border border-divider">
                <p className="text-foreground/80">
                  <strong>Важно:</strong> Расходы по возврату товара надлежащего качества несет покупатель. В случае возврата товара ненадлежащего качества, мы компенсируем расходы на доставку.
                </p>
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-light mb-6">Часто задаваемые <span className="font-medium">вопросы</span></h2>
              
              <Accordion variant="splitted">
                <AccordionItem key="1" aria-label="Можно ли вернуть товар, если я просто передумал?" title="Можно ли вернуть товар, если я просто передумал?">
                  <p className="text-foreground/80">
                    Да, вы можете вернуть товар надлежащего качества в течение 14 дней с момента получения, если сохранены товарный вид, потребительские свойства, пломбы и фабричные ярлыки.
                  </p>
                </AccordionItem>
                <AccordionItem key="2" aria-label="В какой срок осуществляется возврат денежных средств?" title="В какой срок осуществляется возврат денежных средств?">
                  <p className="text-foreground/80">
                    Возврат денежных средств осуществляется в течение 10 рабочих дней с момента получения нами возвращенного товара и заявления на возврат.
                  </p>
                </AccordionItem>
                <AccordionItem key="3" aria-label="Можно ли обменять товар на другой?" title="Можно ли обменять товар на другой?">
                  <p className="text-foreground/80">
                    Да, вы можете обменять товар на аналогичный или другой товар с перерасчетом стоимости. Для этого свяжитесь с нами по телефону или email для оформления заявки на обмен.
                  </p>
                </AccordionItem>
                <AccordionItem key="4" aria-label="Как определяется, является ли дефект производственным браком?" title="Как определяется, является ли дефект производственным браком?">
                  <p className="text-foreground/80">
                    При получении возвращенного товара наши специалисты проводят проверку для определения характера дефекта. Если будет установлено, что дефект возник по вине производителя, мы осуществим замену товара или вернем денежные средства.
                  </p>
                </AccordionItem>
              </Accordion>
            </div>

            <div className="p-6 border border-primary bg-primary/5">
              <h3 className="text-xl font-medium mb-4">Нужна помощь?</h3>
              <p className="text-foreground/80 mb-4">
                Если у вас возникли вопросы по возврату или обмену товара, пожалуйста, свяжитесь с нашей службой поддержки:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Icon icon="lucide:phone" className="text-primary mt-1" width={18} />
                  <span className="text-foreground/80">+7 (995) 881-02-47</span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon icon="lucide:mail" className="text-primary mt-1" width={18} />
                  <a 
                  href="mailto:info@safonova-brand.ru" 
                  className="text-foreground/70 hover:text-foreground relative group overflow-hidden"
                >
                  <span className="transition-colors duration-300 ease-out">info@safonova-brand.ru</span>
                  <span className="absolute left-0 bottom-0 w-0 h-px bg-foreground transition-all duration-300 ease-in-out group-hover:w-full"></span>
                </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};